import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Clock, Users, BarChart3, Calendar, CheckCircle, 
  ArrowRight, Play, Shield, Zap, Globe, Star,
  ChevronDown, Menu, X, User, Building2, Crown,
  Monitor, Apple
} from 'lucide-react';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import appLogo from '../assets/logo.svg';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [pricingCycle, setPricingCycle] = useState('monthly');

  const features = [
    { icon: Clock, titleKey: 'landing.featureTracking', descKey: 'landing.featureTrackingDesc' },
    { icon: Users, titleKey: 'landing.featureTeam', descKey: 'landing.featureTeamDesc' },
    { icon: BarChart3, titleKey: 'landing.featureReports', descKey: 'landing.featureReportsDesc' },
    { icon: Calendar, titleKey: 'landing.featureTimesheets', descKey: 'landing.featureTimesheetsDesc' },
    { icon: Shield, titleKey: 'landing.featureLeave', descKey: 'landing.featureLeaveDesc' },
    { icon: Zap, titleKey: 'landing.featureCalendar', descKey: 'landing.featureCalendarDesc' }
  ];

  const faqs = [
    { q: t('landing.faqTrial'), a: t('landing.faqTrialAnswer') },
    { q: t('landing.faqTeam'), a: t('landing.faqTeamAnswer') },
    { q: t('landing.faqPayment'), a: t('landing.faqPaymentAnswer') },
    { q: t('landing.faqExport'), a: t('landing.faqExportAnswer') },
    { q: t('landing.faqSecurity'), a: t('landing.faqSecurityAnswer') }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <img src={appLogo} alt="Inspiro Tracker" className="w-10 h-10 rounded-lg object-contain" />
              <span className="text-2xl font-bold">Inspiro Tracker</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-400 hover:text-white transition-colors">{t('landing.features')}</a>
              <a href="#pricing" className="text-slate-400 hover:text-white transition-colors">{t('landing.pricing')}</a>
              <a href="#download" className="text-slate-400 hover:text-white transition-colors">Download</a>
              <a href="#faq" className="text-slate-400 hover:text-white transition-colors">{t('landing.faq')}</a>
              <LanguageSwitcher />
              <button 
                onClick={() => navigate('/login')}
                className="text-slate-400 hover:text-white transition-colors"
              >
                {t('landing.login')}
              </button>
              <button 
                onClick={() => navigate('/register')}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
              >
                {t('landing.getStarted')}
              </button>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-900 border-t border-slate-800">
            <div className="px-4 py-4 space-y-4">
              <a href="#features" className="block text-slate-400 hover:text-white">{t('landing.features')}</a>
              <a href="#pricing" className="block text-slate-400 hover:text-white">{t('landing.pricing')}</a>
              <a href="#faq" className="block text-slate-400 hover:text-white">{t('landing.faq')}</a>
              <button 
                onClick={() => navigate('/login')}
                className="block w-full text-left text-slate-400 hover:text-white"
              >
                {t('landing.login')}
              </button>
              <button 
                onClick={() => navigate('/register')}
                className="block w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium text-center"
              >
                {t('landing.getStarted')}
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/10 border border-blue-500/20 rounded-full text-blue-400 text-sm mb-8">
            <Star className="w-4 h-4" />
            <span>Trusted by 500+ teams across India</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            {t('landing.heroTitle')}<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">
              {t('landing.heroTitleHighlight')}
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-10">
            {t('landing.heroSubtitle')}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button 
              onClick={() => navigate('/register')}
              className="w-full sm:w-auto px-8 py-4 bg-blue-600 hover:bg-blue-700 rounded-xl font-semibold text-lg transition-all hover:scale-105 flex items-center justify-center gap-2"
            >
              <Play className="w-5 h-5" />
              {t('landing.getStarted')}
            </button>
            <button 
              onClick={() => navigate('/login')}
              className="w-full sm:w-auto px-8 py-4 bg-slate-800 hover:bg-slate-700 rounded-xl font-semibold text-lg transition-colors flex items-center justify-center gap-2"
            >
              {t('landing.login')}
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>

          <p className="text-slate-500 text-sm mt-4">
            {t('landing.noCreditCard')}
          </p>
        </div>

        {/* Dashboard Preview */}
        <div className="max-w-5xl mx-auto mt-16 relative">
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent z-10 pointer-events-none" />
          <div className="rounded-2xl border border-slate-800 overflow-hidden shadow-2xl shadow-blue-500/10">
            <div className="bg-slate-900 p-1">
              <div className="flex items-center gap-2 px-3 py-2">
                <div className="w-3 h-3 rounded-full bg-red-50 dark:bg-red-900/30" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-50 dark:bg-green-900/30" />
              </div>
            </div>
            <div className="bg-slate-900/50 p-8">
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <div className="text-slate-500 text-sm mb-1">Today</div>
                  <div className="text-2xl font-bold text-blue-400">4h 32m</div>
                </div>
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <div className="text-slate-500 text-sm mb-1">This Week</div>
                  <div className="text-2xl font-bold text-emerald-400">28h 15m</div>
                </div>
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <div className="text-slate-500 text-sm mb-1">Projects</div>
                  <div className="text-2xl font-bold text-purple-400">12</div>
                </div>
              </div>
              <div className="bg-slate-800/30 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <div className="font-medium">Working on: Product Design</div>
                    <div className="text-sm text-slate-500">Client: Acme Corp</div>
                  </div>
                </div>
                <div className="text-2xl font-mono text-emerald-400">01:45:32</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {t('landing.featuresTitle')}
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              {t('landing.featuresSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6 hover:border-blue-500/50 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{t(feature.titleKey)}</h3>
                <p className="text-slate-400">{t(feature.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {t('landing.pricingTitle')}
            </h2>
            <p className="text-slate-400 text-lg mb-4">
              {t('landing.pricingSubtitle')}
            </p>

            {/* Billing Cycle Toggle */}
            <div className="inline-flex items-center p-1 bg-slate-800 rounded-xl" data-testid="landing-billing-toggle">
              <button
                onClick={() => setPricingCycle('monthly')}
                className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  pricingCycle === 'monthly'
                    ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
                data-testid="landing-monthly-btn"
              >
                Monthly
              </button>
              <button
                onClick={() => setPricingCycle('yearly')}
                className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                  pricingCycle === 'yearly'
                    ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
                data-testid="landing-yearly-btn"
              >
                Yearly
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-400 text-[11px] font-bold border border-emerald-500/30">
                  Save 10%
                </span>
              </button>
            </div>
          </div>

          {/* Three-Tier Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto items-start">
            
            {/* Individual Plan */}
            <div className="relative bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6 lg:p-8 hover:border-slate-600 transition-all" data-testid="pricing-individual">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-slate-700/50 flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-300" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Individual</h3>
                  <p className="text-slate-500 text-xs">For solo professionals</p>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold">Free</span>
                </div>
                <p className="text-slate-500 text-sm mt-1">Forever, no credit card needed</p>
              </div>

              <button
                onClick={() => navigate('/register')}
                className="w-full py-3 px-4 rounded-xl font-semibold text-sm border border-slate-600 text-slate-300 hover:bg-slate-700/50 hover:text-white transition-all mb-8"
                data-testid="pricing-individual-cta"
              >
                Get Started Free
              </button>

              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">What's included</p>
                {[
                  'Personal time tracking',
                  'Unlimited projects',
                  'Client creation & management',
                  'Basic reports',
                  'Calendar view',
                  'Manual time entries',
                  'Timesheet management',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-sm text-slate-400">
                    <CheckCircle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Business Plan - Most Popular */}
            <div className="relative bg-gradient-to-b from-blue-500/10 via-slate-800/60 to-slate-900/60 border-2 border-blue-500/40 rounded-2xl p-6 lg:p-8 shadow-lg shadow-blue-500/5" data-testid="pricing-business">
              {/* Most Popular Badge */}
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                <div className="inline-flex items-center gap-1.5 px-4 py-1 bg-blue-600 rounded-full text-xs font-bold text-white shadow-lg shadow-blue-600/30">
                  <Zap className="w-3 h-3" />
                  Most Popular
                </div>
              </div>

              <div className="flex items-center gap-3 mb-6 mt-2">
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/15 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Business</h3>
                  <p className="text-slate-500 text-xs">For growing teams</p>
                </div>
              </div>

              <div className="mb-1">
                {pricingCycle === 'yearly' ? (
                  <>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-lg line-through text-slate-600 dark:text-slate-300 font-medium">₹2,388</span>
                      <span className="text-4xl font-bold text-white">₹2,148</span>
                    </div>
                    <p className="text-slate-400 text-sm">/user/year</p>
                    <p className="text-emerald-400 text-xs font-semibold mt-1">Save 10% with annual billing</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-lg line-through text-slate-600 dark:text-slate-300 font-medium">₹199</span>
                      <span className="text-4xl font-bold text-white">₹99</span>
                    </div>
                    <p className="text-slate-400 text-sm">/user/month</p>
                  </>
                )}
              </div>
              
              <p className="text-slate-500 text-sm mt-2 mb-5">
                {pricingCycle === 'yearly' 
                  ? 'Billed as ₹2,148/user/year (was ₹2,388)'
                  : `or ₹2,148/user/year with annual billing`}
              </p>

              <button
                onClick={() => navigate('/register')}
                className="w-full py-3 px-4 rounded-xl font-semibold text-sm bg-blue-600 hover:bg-blue-700 text-white transition-all hover:scale-[1.02] mb-8"
                data-testid="pricing-business-cta"
              >
                Start 14-Day Free Trial
              </button>

              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Everything in Individual, plus</p>
                {[
                  'Unlimited projects',
                  'Unlimited team members',
                  'Timesheet approvals',
                  'Detailed reports & exports',
                  'Calendar integrations',
                  'Leave management',
                  'Screenshot tracking',
                  'Client management',
                  'Priority email support',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Premium Plan */}
            <div className="relative bg-gradient-to-b from-purple-500/8 via-slate-800/40 to-slate-900/40 border border-purple-500/25 rounded-2xl p-6 lg:p-8 hover:border-purple-500/40 transition-all" data-testid="pricing-premium">
              {/* Enterprise Badge */}
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                <div className="inline-flex items-center gap-1.5 px-4 py-1 bg-gradient-to-r from-purple-600 to-purple-500 rounded-full text-xs font-bold text-white shadow-lg shadow-purple-500/20">
                  <Crown className="w-3 h-3" />
                  Enterprise Ready
                </div>
              </div>

              <div className="flex items-center gap-3 mb-6 mt-2">
                <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-900/15 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Premium</h3>
                  <p className="text-slate-500 text-xs">For enterprises</p>
                </div>
              </div>

              <div className="mb-1">
                {pricingCycle === 'yearly' ? (
                  <>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-lg line-through text-slate-600 dark:text-slate-300 font-medium">₹3,240</span>
                      <span className="text-4xl font-bold text-white">₹1,620</span>
                    </div>
                    <p className="text-slate-400 text-sm">/user/year</p>
                    <p className="text-emerald-400 text-xs font-semibold mt-1">Save 10% yearly + 50% launch offer</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-lg line-through text-slate-600 dark:text-slate-300 font-medium">₹300</span>
                      <span className="text-4xl font-bold text-white">₹150</span>
                    </div>
                    <p className="text-slate-400 text-sm">/user/month</p>
                    <p className="text-amber-400 text-xs font-semibold mt-1">50% off — Limited Time Offer</p>
                  </>
                )}
              </div>

              <p className="text-slate-500 text-sm mt-2 mb-5">
                {pricingCycle === 'yearly'
                  ? 'Billed as ₹1,620/user/year (was ₹3,600)'
                  : `or ₹1,620/user/year with annual billing`}
              </p>

              <button
                onClick={() => navigate('/register')}
                className="w-full py-3 px-4 rounded-xl font-semibold text-sm border border-purple-500/40 text-purple-300 hover:bg-purple-50 dark:bg-purple-900/10 hover:text-white transition-all mb-8"
                data-testid="pricing-premium-cta"
              >
                Start 14-Day Free Trial
              </button>

              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Everything in Business, plus</p>
                {[
                  'Unlimited users',
                  'Custom domain',
                  'Custom email notifications',
                  'Region-based data storage',
                  'Custom branding & white-label',
                  'Priority support with SLA',
                  'Dedicated storage configurations',
                  'Advanced analytics & insights',
                  'API access',
                  'SSO / SAML integration',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Volume / Custom Pricing */}
          <div className="mt-10 text-center">
            <p className="text-slate-500 text-sm">
              Need custom pricing for 100+ users? <a href="mailto:sales@inspirotrack.com" className="text-blue-400 hover:underline font-medium">Contact our sales team</a>
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {t('landing.faqTitle')}
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-slate-800/30 border border-slate-700/50 rounded-xl overflow-hidden"
              >
                <button
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-medium">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 text-slate-400">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download Desktop App Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" id="download" data-testid="download-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-6">
            <Monitor className="w-4 h-4" />
            Desktop App
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Download Inspiro Tracker for Desktop
          </h2>
          <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">
            Track time seamlessly with our native desktop app. Automatic screenshots, idle detection, and offline support — all built in.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://api.inspiroworks.com/downloads/InspiroTracker.exe"
              className="flex items-center gap-3 px-8 py-4 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 rounded-xl font-semibold transition-all hover:scale-105 w-full sm:w-auto justify-center"
              data-testid="download-windows-btn"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/></svg>
              <div className="text-left">
                <div className="text-xs text-slate-400">Download for</div>
                <div className="text-base">Windows</div>
              </div>
            </a>
            <a
              href="https://api.inspiroworks.com/downloads/InspiroTracker.dmg"
              className="flex items-center gap-3 px-8 py-4 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 rounded-xl font-semibold transition-all hover:scale-105 w-full sm:w-auto justify-center"
              data-testid="download-mac-btn"
            >
              <Apple className="w-6 h-6" />
              <div className="text-left">
                <div className="text-xs text-slate-400">Download for</div>
                <div className="text-base">macOS</div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            {t('landing.ctaTitle')}
          </h2>
          <p className="text-slate-400 text-lg mb-8">
            {t('landing.ctaSubtitle')}
          </p>
          <button 
            onClick={() => navigate('/register')}
            className="px-8 py-4 bg-blue-600 hover:bg-blue-700 rounded-xl font-semibold text-lg transition-all hover:scale-105"
          >
            {t('landing.getStarted')}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={appLogo} alt="Inspiro Tracker" className="w-10 h-10 rounded-lg object-contain" />
              <span className="text-xl font-bold">Inspiro Tracker</span>
            </div>
            <div className="flex items-center gap-6 text-slate-400 text-sm">
              <a href="/privacy" className="hover:text-white">Privacy Policy</a>
              <a href="/terms" className="hover:text-white">Terms of Service</a>
              <a href="mailto:support@inspiroworks.com" className="hover:text-white">Contact</a>
            </div>
            <div className="text-slate-500 text-sm">
              &copy; {new Date().getFullYear()} INPW Technologies Pvt Ltd. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
